import DehazeIcon from '@mui/icons-material/Dehaze';
import { Button, Checkbox, CircularProgress, FormControlLabel, Grid, Typography } from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { AgCheckboxGroupPanel } from '../../common/AgCheckboxGroupPanel';
import { PopperButton } from '../../common/PopperButton';
import { RootState } from '../../store';
import PricesMenuButton from '../price/PricesMenuButton';
import { PricelistDialog } from '../pricelist/PricelistDialog';
import StockDatePicker from '../stock/StockDatePicker';
import { pricePlanInitialColumns } from './basePriceTypes';
import {
    getBasePriceSelectedWarehouseDate,
    getBasePriceTable,
    getBasePriceTableVisibleColumnKeys,
    getIsLoadingBasePrices,
    getSelectedBasePricelist,
    getSelectedPricelistId,
    getShowActivePricelist,
    getShowAllArticles
} from './redux/basePriceSelectors';
import {
    basePriceTableActions,
    pricelistSelected,
    setIsExporting,
    showActivePricelistChanged,
    showAllArticlesChanged,
    warehouseDateSelected
} from './redux/basePriceSlices';

const mapStateToProps = (state: RootState) => ({
    showAllArticles: getShowAllArticles(state),
    selectedPricelistId: getSelectedPricelistId(state),
    columnKeys: getBasePriceTableVisibleColumnKeys(state),
    selectedWarehouseDate: getBasePriceSelectedWarehouseDate(state),
    showActivePricelist: getShowActivePricelist(state),
    selectedBasePricelist: getSelectedBasePricelist(state),
    isLoading: getIsLoadingBasePrices(state),
    basePriceTable: getBasePriceTable(state)
});

type Props = ReturnType<typeof mapStateToProps>;

// const styles = makeStyles({
//     selectedPricelistLabel: {
//         padding: 7
//     },
//     hide: {
//         visibility: 'hidden'
//     }
// });

const BasePriceButtons = ({
    showAllArticles,
    selectedPricelistId,
    columnKeys,
    selectedWarehouseDate,
    selectedBasePricelist,
    showActivePricelist,
    isLoading,
    basePriceTable
}: Props) => {
    const [isPricelistDialogOpen, setPricelistDialogOpen] = useState(false);

    const dispatch = useDispatch();

    const handleOpenPricelistDialog = () => setPricelistDialogOpen(true);
    const handlePricelistDialogClosed = () => setPricelistDialogOpen(false);

    const handleShowAllArticles = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(showAllArticlesChanged(event.target.checked));
    };

    const handlePricelistSelected = (pricelistId: number | number[]) => {
        if (!Array.isArray(pricelistId)) {
            dispatch(pricelistSelected(pricelistId));
        }
    };

    const handleColumnsSelected = (newColumns: string[]) => {
        dispatch(basePriceTableActions.columnsSelected(newColumns));
    };

    const handleSetSelectedWarehouseAndDate = (date: Date | null, warehouseId: number | null) => {
        if (date != null && warehouseId != null) {
            dispatch(
                warehouseDateSelected({
                    date: date.toISOString(),
                    warehouse: warehouseId
                })
            );
        }
    };

    const handleShowActivePricelist = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(showActivePricelistChanged(event.target.checked));
    };

    const onBtnExport = () => {
        dispatch(setIsExporting(true));
    };

    return (
        <Grid container alignItems="flex-start" direction="row" wrap="nowrap" justifyContent="space-between">
            <Grid item>
                <Grid container spacing={2}>
                    <Grid item>
                        <Button variant="contained" color="primary" onClick={handleOpenPricelistDialog}>
                            Choose base pricelist
                        </Button>
                    </Grid>

                    {selectedBasePricelist != null && (
                        <Grid item>
                            <Typography variant="subtitle1" noWrap={true} sx={{ p: 1 }}>
                                {`${selectedBasePricelist.pricelistRef} - ${selectedBasePricelist.name}`}
                            </Typography>
                        </Grid>
                    )}

                    <Grid item>
                        <FormControlLabel
                            control={<Checkbox checked={showAllArticles} onChange={handleShowAllArticles} />}
                            label="Show all products"
                        />
                    </Grid>

                    <Grid item>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showActivePricelist}
                                    onChange={handleShowActivePricelist}
                                />
                            }
                            label="Show active pricelist"
                        />
                    </Grid>

                    <Grid item>
                        <StockDatePicker
                            selectedDate={new Date(selectedWarehouseDate.date)}
                            selectedWarehouse={selectedWarehouseDate.warehouse}
                            setSelectedWarehouseAndDate={handleSetSelectedWarehouseAndDate}
                        />
                    </Grid>
                    <Grid item>
                        <PricesMenuButton
                            onDownloadClicked={onBtnExport}
                            showTooltipsChangedAction={basePriceTableActions.showTooltipsChanged}
                            tooltipsVisible={basePriceTable.showTooltips}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <CircularProgress sx={{ visibility: !isLoading ? 'hidden' : undefined }} />
                    </Grid>

                    <Grid item xs={6}>
                        <PopperButton btnIcon={<DehazeIcon />} btnTitle="Choose columns">
                            <AgCheckboxGroupPanel
                                available={pricePlanInitialColumns}
                                selected={columnKeys.map(c => c.key)}
                                setSelected={handleColumnsSelected}
                            />
                        </PopperButton>
                    </Grid>
                </Grid>
            </Grid>

            {isPricelistDialogOpen && (
                <PricelistDialog
                    handleClose={handlePricelistDialogClosed}
                    multiSelect={false}
                    selectedPricelistIds={selectedPricelistId ?? undefined}
                    pricelistsSelected={handlePricelistSelected}
                    showBasePricelistsOnly={true}
                />
            )}
        </Grid>
    );
};

export default connect(mapStateToProps)(BasePriceButtons);
